import { action, observable } from 'mobx';
import { FormulaFormVM } from '../template/FormulaFormVM';
import { HydraulicFormulaController } from 'cd-eft/services/eftSetApi';
import { SurfaceInletRequestDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { toTitleFromUnderscore } from 'cd-eft/utils/stringUtilities';

interface ISurfaceInletDrainageForm {
  runoff: {
    drainedArea: string;
    drainageCoefficient: string;
    qRequired: string;
  };
  elevations: {
    waterElev: string;
    soilElevRiser: string;
    mainLineElevation: string;
    mainLineDiameter: string;
  };
  riserMaterial: {
    perforated: boolean;
    rmMaterial: string;
    pressurizable: boolean;
    rmPipeSize: string;
    rmManningsn: string;
  };
  inletTop: {
    guard: string;
    topOpening: string;
    enableTopOpening: boolean;
    pluggedFraction: string;
    qTop: string;
  };
  riserPerforations: {
    holesPerft: string;
    height: string;
    perfDiameter: string;
    perfWidth: string;
    perfHeight: string;
    perfFraction: string;
    qPerf: string;
  };
  orificePlate: {
    useOrifice: boolean;
    orificeDepth: string;
    userDefinedSize: boolean;
    diameter: string;
    qOrifice: string;
  };
  offsetPipe: {
    useOffset: boolean;
    offsetLength: string;
    elbowElevation: string;
    opPerforated: boolean;
    opMaterial: string;
    opPressurizable: boolean;
    opPipeSize: string;
    opManningsn: string;
    qOffset: string;
  };
}
export class SurfaceInletDrainageVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  constructor(props) {
    super(props);
    this.formLabelsForExport = {
      drainedArea: 'Drained Area (ac)',
      drainageCoefficient: 'Drainage Coefficient',
      waterElev: 'Water Elevation (ft)',
      soilElevRiser: 'Soil Elevation at Riser (ft)',
      mainLineElevation: 'Main-Line Elevation (ft)',
      mainLineDiameter: 'Main-Line Diameter (in)',
      rmMaterial: 'Riser Material - Material',
      perforated: 'Riser Material - Perforated',
      pressurizable: 'Riser Material - Pressurizable',
      rmPipeSize: 'Riser Material - Pipe size (in)',
      rmManningsn: "Riser Material - Manning's n",
      guard: 'Guard',
      topOpening: 'Top Opening (in)',
      pluggedFraction: 'Plugged Fraction',
      holesPerft: 'Holes per ft',
      height: 'Height (ft)',
      roundPerf: 'Round Perf',
      rectangularPerf: 'Rectangular Perf',
      perfDiameter: 'Perf. Diam (in)',
      perfWidth: 'Perf. Width (in)',
      perfHeight: 'Perf. Height (in)',
      perfFraction: 'Plugged Perf. Fraction',
      useOrifice: 'Use Orifice',
      orificeDepth: 'Orifice Depth (in)',
      diameter: 'Diameter (in)',
      useOffset: 'Use Offset',
      offsetLength: 'Offset Length (ft)',
      elbowElevation: 'Elbow Elevation (ft)',
      opMaterial: 'Offset Pipe - Material',
      opPerforated: 'Offset Pipe - Perforated',
      opPressurizable: 'Offset Pipe - Pressurizable',
      opPipeSize: 'Offset Pipe - Pipe Size (in)',
      opManningsn: "Offset Pipe - Manning's n",
    };

    this.resultLabelsForExport = {};
  }
  @observable SurfaceInletDrainageFormData: ISurfaceInletDrainageForm = {
    runoff: {
      drainedArea: "0.00",
      drainageCoefficient: '',
      qRequired: "0.00",
    },
    elevations: {
      waterElev: "0.00",
      soilElevRiser: "0.00",
      mainLineElevation: "0.00",
      mainLineDiameter: "0.00",
    },
    riserMaterial: {
      perforated: false,
      rmMaterial: '',
      pressurizable: false,
      rmPipeSize: '0.00',
      rmManningsn: "0.0000",
    },
    inletTop: {
      guard: '',
      topOpening: "0.00",
      enableTopOpening: false,
      pluggedFraction: "0.00",
      qTop: "0.00",
    },
    riserPerforations: {
      holesPerft: "0.00",
      height: "0.00",
      perfDiameter: "0.00",
      perfWidth: "0.00",
      perfHeight: "0.00",
      perfFraction: "0.00",
      qPerf: '',
    },
    orificePlate: {
      useOrifice: false,
      orificeDepth: "0.00",
      userDefinedSize: false, //this variable is not in API
      diameter: "0.00",
      qOrifice: '',
    },
    offsetPipe: {
      useOffset: false,
      offsetLength: "0.00",
      elbowElevation: "0.00",
      opPerforated: false,
      opMaterial: '',
      opPressurizable: false,
      opPipeSize: "0.00",
      opManningsn: "0.0000",
      qOffset: '',
    },
  };
  @observable formValidation = {
    hasError: true,
    runoff: {
      drainedArea: false,
      drainageCoefficient: false,
      qRequired: false,
    },
    elevations: {
      waterElev: false,
      soilElevRiser: false,
      mainLineElevation: false,
      mainLineDiameter: false,
    },
    riserMaterial: {
      // perforated : false,
      rmMaterial: false,
      // pressurizable : false,
      rmPipeSize: false,
      rmManningsn: false,
    },
    inletTop: {
      guard: false,
      topOpening: false,
      pluggedFraction: false,
      qTop: false,
    },
    riserPerforations: {
      holesPerft: false,
      height: false,
      perfDiameter: false,
      perfWidth: false,
      perfHeight: false,
      perfFraction: false,
      qPerf: false,
    },
    orificePlate: {
      orificeDepth: false,
      diameter: false,
    },
    offsetPipe: {
      offsetLength: false,
      elbowElevation: false,
      opMaterial: false,
      opPipeSize: false,
      opManningsn: false,
    },
    formErrors: {
      drainedArea: '',
      drainageCoefficient: '',
      waterElev: '',
      soilElevRiser: '',
      mainLineElevation: '',
      mainLineDiameter: '',
      rmMaterial: '',
      enableMaterial: '',
      rmPipeSize: '',
      enablePipeSize: '',
      rmManningsn: '',
      guard: '',
      topOpening: '',
      pluggedFraction: '',
      holesPerft: '',
      height: '',
      perfDiameter: '',
      perfWidth: '',
      perfHeight: '',
      perfFraction: '',
      orificeDepth: '',
      diameter: '',
      offsetLength: '',
      elbowElevation: '',
      opMaterial: '',
      opPipeSize: '',
      opManningsn: '',
    },
  };

  @observable surfaceInletDrainageFormInputSnapshot: ISurfaceInletDrainageForm | undefined;
  // @observable SurfaceInletDrainageResults: SurfaceInletResponseDTO | undefined;
  @observable SurfaceInletDrainageResults: any | undefined = [];
  @observable SurfaceInletDrainageRunoffOptions: any = [];
  @observable SurfaceInletDrainageInletTopOptions: any = [];
  @observable SurfaceInletDrainageRmMaterialOptions: any = [];
  @observable SurfaceInletDrainageRmPipesizeOptions: any = [];
  @observable SurfaceInletDrainageOpMaterialOptions: any = [];
  @observable SurfaceInletDrainageOpPipesizeOptions: any = [];
  @observable surfaceInletRadioControl: number = 0;
  @observable hasResults: boolean = false;

  @action.bound SurfaceInletDrainageErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    if (field === 'guard' || field === 'drainageCoefficient') {
      return errorMessage?.length === 0 ? 'guard' : 'guard usa-input--error';
    }
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }

  get roundPerfControl(): boolean {
    return this.surfaceInletRadioControl === 0;
  }
  get rectangularPerfControl(): boolean {
    return this.surfaceInletRadioControl === 1;
  }

  @action.bound setFixedValue(name, fixed) {
    const event1 = {
      target: {
        name: name,
        value: this.roundingHelperString(fixed, name.includes('manningsn') ? 4 : 2),
      },
    };
    this.handleInputChange(event1, true);
  }

  @action.bound toggleEnableDisableInputs(name) {
    switch (name) {
      case 'rm-perforated':
        this.SurfaceInletDrainageFormData.riserMaterial.perforated = !this.SurfaceInletDrainageFormData.riserMaterial
          .perforated;
        break;
      case 'rm-pressurizable':
        this.SurfaceInletDrainageFormData.riserMaterial.pressurizable = !this.SurfaceInletDrainageFormData.riserMaterial
          .pressurizable;
        break;
      case 'useOrifice':
        this.SurfaceInletDrainageFormData.orificePlate.useOrifice = !this.SurfaceInletDrainageFormData.orificePlate
          .useOrifice;
        if (!this.SurfaceInletDrainageFormData.orificePlate.useOrifice) {
          this.SurfaceInletDrainageFormData.orificePlate = {
            ...this.SurfaceInletDrainageFormData.orificePlate,
            orificeDepth: this.defaultValues.orificeDepth,
            userDefinedSize: true,
            diameter: this.defaultValues.orificeDiam,
            qOrifice: '',
          }
        }
        break;
      case 'userDefinedSize':
        this.SurfaceInletDrainageFormData.orificePlate.userDefinedSize = !this.SurfaceInletDrainageFormData.orificePlate
          .userDefinedSize;
        break;
      case 'useOffset':
        this.SurfaceInletDrainageFormData.offsetPipe.useOffset = !this.SurfaceInletDrainageFormData.offsetPipe
          .useOffset;
        if (!this.SurfaceInletDrainageFormData.offsetPipe.useOffset) {
          this.SurfaceInletDrainageFormData.offsetPipe = {
            ...this.SurfaceInletDrainageFormData.offsetPipe,
            offsetLength: this.defaultValues.offsetLength,
            elbowElevation: this.defaultValues.elbowElev,
            opPerforated: this.defaultValues.offsetPerforated,
            opMaterial: '',
            opPressurizable: this.defaultValues.offsetPressurizable,
            opPipeSize: "0.00",
            opManningsn: "0.0000",
            qOffset: '',
          }
        }
        break;
      case 'op-perforated':
        this.SurfaceInletDrainageFormData.offsetPipe.opPerforated = !this.SurfaceInletDrainageFormData.offsetPipe
          .opPerforated;
        break;
      case 'op-pressurizable':
        this.SurfaceInletDrainageFormData.offsetPipe.opPressurizable = !this.SurfaceInletDrainageFormData.offsetPipe
          .opPressurizable;
        break;
    }
  }

  @action.bound handleInputChange(event, within?: boolean) {
    const { name } = event.target;
    let value = event.target.value;
    if (!within && name !== 'op-material' && name !== 'rm-material' && name !== 'guard' && value.length > 7) {
      return;
    }

    switch (name) {
      case 'drained-area':
        this.SurfaceInletDrainageFormData.runoff.drainedArea = value;
        break;
      case 'drainage-coefficient':
        this.SurfaceInletDrainageFormData.runoff.drainageCoefficient = value;
        break;
      case 'water-elevation':
        this.SurfaceInletDrainageFormData.elevations.waterElev = value;
        break;
      case 'soil-elevation-riser':
        this.SurfaceInletDrainageFormData.elevations.soilElevRiser = value;
        break;
      case 'main-line-elevation':
        this.SurfaceInletDrainageFormData.elevations.mainLineElevation = value;
        break;
      case 'main-line-diameter':
        this.SurfaceInletDrainageFormData.elevations.mainLineDiameter = value;
        break;
      case 'rm-material':
        this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial = value;
        const riserPipeSizeObj = this.defaultValues.riserMaterial.find((reqVal) => reqVal.riserMaterialName === value);
        if (riserPipeSizeObj) {
          this.SurfaceInletDrainageRmPipesizeOptions = riserPipeSizeObj.riserPipeSize.map((val, index) => ({
            key: index,
            name: val.subRiserPipeSize,
            value: val.subRiserPipeSize,
          }));
          this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize = '0.00';
          this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn = "0.0000";
        } else {
          this.SurfaceInletDrainageRmPipesizeOptions = [];
          this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize = '0.00';
          this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn = "0.0000";
        }
        break;
      case 'rm-pipe-size':
        this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize = value;
        if (value !== '') {
          const riserPipeSizeObjforMannings = this.defaultValues.riserMaterial.find(
            (reqVal) => reqVal.riserMaterialName === this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial
          );
          this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn = this.roundingHelperString(riserPipeSizeObjforMannings.riserPipeSize.find(
            (pipeSize) => pipeSize.subRiserPipeSize === parseFloat(value)
          ).riserPipeManningsN, 4);
          this.formValidation.riserMaterial.rmManningsn = false;
          this.formValidation.formErrors.rmManningsn = '';
          this.formValidation.hasError = false;
        } else {
          this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn = "0.0000";
          this.formValidation.riserMaterial.rmManningsn = true;
          this.formValidation.formErrors.rmManningsn = ' ';
          this.formValidation.hasError = true;
        }
        break;
      case 'rm-pipe-size-box':
        this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize = value;
        break;
      case 'rm-manningsn':
        this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn = value;
        break;
      case 'guard':
        this.SurfaceInletDrainageFormData.inletTop.guard = value;
        if (value === 'CAPPED') {
          this.SurfaceInletDrainageFormData.inletTop.enableTopOpening = true;
        } else {
          this.formValidation.inletTop.topOpening = false;
          this.formValidation.formErrors.topOpening = '';
          this.formValidation.hasError = false;
          this.SurfaceInletDrainageFormData.inletTop.enableTopOpening = false;
        }
        break;
      case 'top-opening':
        this.SurfaceInletDrainageFormData.inletTop.topOpening = value;
        break;
      case 'plugged-fraction':
        this.SurfaceInletDrainageFormData.inletTop.pluggedFraction = value;
        break;
      case 'holes-per-ft':
        this.SurfaceInletDrainageFormData.riserPerforations.holesPerft = value;
        break;
      case 'height':
        this.SurfaceInletDrainageFormData.riserPerforations.height = value;
        break;
      case 'round-perf':
        this.surfaceInletRadioControl = 0;
        break;
      case 'rectangular-perf':
        this.surfaceInletRadioControl = 1;
        break;
      case 'perf-diam':
        this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter = value;
        break;
      case 'perf-width':
        this.SurfaceInletDrainageFormData.riserPerforations.perfWidth = value;
        break;
      case 'perf-height':
        this.SurfaceInletDrainageFormData.riserPerforations.perfHeight = value;
        break;
      case 'plugged-perf-fraction':
        this.SurfaceInletDrainageFormData.riserPerforations.perfFraction = value;
        break;
      case 'orifice-depth':
        this.SurfaceInletDrainageFormData.orificePlate.orificeDepth = value;
        break;
      case 'diameter':
        this.SurfaceInletDrainageFormData.orificePlate.diameter = value;
        break;
      case 'offset-length':
        this.SurfaceInletDrainageFormData.offsetPipe.offsetLength = value;
        break;
      case 'elbow-elevation':
        this.SurfaceInletDrainageFormData.offsetPipe.elbowElevation = value;
        break;
      case 'op-material':
        this.SurfaceInletDrainageFormData.offsetPipe.opMaterial = value;
        const opRiserPipeSizeObj = this.defaultValues.offsetMaterial.find(
          (reqVal) => reqVal.offsetMaterialName === value
        );
        if (opRiserPipeSizeObj) {
          this.SurfaceInletDrainageOpPipesizeOptions = opRiserPipeSizeObj.offsetPipeSize.map((val, index) => ({
            key: index,
            name: val.suboffsetPipeSize,
            value: val.suboffsetPipeSize,
          }));
          this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize = "0.00";
          this.SurfaceInletDrainageFormData.offsetPipe.opManningsn = "0.0000";
        } else {
          this.SurfaceInletDrainageOpPipesizeOptions = [];
          this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize = "0.00";
          this.SurfaceInletDrainageFormData.offsetPipe.opManningsn = "0.0000";
        }
        break;
      case 'op-pipe-size':
        this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize = value;
        if (value !== '') {
          const opPipeSizeObjforMannings = this.defaultValues.offsetMaterial.find(
            (reqVal) => reqVal.offsetMaterialName == this.SurfaceInletDrainageFormData.offsetPipe.opMaterial
          );
          this.SurfaceInletDrainageFormData.offsetPipe.opManningsn = this.roundingHelperString(opPipeSizeObjforMannings.offsetPipeSize.find(
            (pipeSize) => pipeSize.suboffsetPipeSize === parseFloat(value)
          ).offsetPipeManningsN, 4);
          this.formValidation.offsetPipe.opManningsn = false;
          this.formValidation.formErrors.opManningsn = '';
          this.formValidation.hasError = false;
        } else {
          this.SurfaceInletDrainageFormData.offsetPipe.opManningsn = "0.0000";
          this.formValidation.offsetPipe.opManningsn = true;
          this.formValidation.formErrors.opManningsn = ' ';
          this.formValidation.hasError = true;
        }
        break;
      case 'op-pipe-size-box':
        this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize = value;
        break;
      case 'op-manningsn':
        this.SurfaceInletDrainageFormData.offsetPipe.opManningsn = value;
        break;
    }
  }

  @action.bound async validateInput(name, value) {
    let val;
    const exclude = ['drainage-coefficient', 'op-material', 'rm-material', 'guard', 'rm-pipe-size', 'op-pipe-size'];
    if (!exclude.includes(name)) {
      if (name.includes('manningsn')) {
        val = this.roundingHelperNumber(parseFloat(value), 4);
      } else if (value !== undefined && value !== '') {
        val = this.roundingHelperNumber(parseFloat(value), 2);
      } else {
        val = 0;
      }
      if (val !== undefined && val !== '') {
        this.setFixedValue(name, val);
      }
    } else val = value;
    switch (name) {
      case 'drained-area':
        if (value === '' || val <= 0) {
          this.formValidation.runoff.drainedArea = true;
          this.formValidation.formErrors.drainedArea = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.drainedArea.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.runoff.drainedArea = false;
          this.formValidation.formErrors.drainedArea = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'drainage-coefficient':
        if (value === '') {
          this.formValidation.runoff.drainageCoefficient = true;
          this.formValidation.formErrors.drainageCoefficient = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.runoff.drainageCoefficient = false;
          this.formValidation.formErrors.drainageCoefficient = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'water-elevation':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.elevations.waterElev = true;
          this.formValidation.formErrors.waterElev = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.waterElev.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.elevations.waterElev = false;
          this.formValidation.formErrors.waterElev = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'soil-elevation-riser':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.elevations.soilElevRiser = true;
          this.formValidation.formErrors.soilElevRiser = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.soilElevRiser.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.elevations.soilElevRiser = false;
          this.formValidation.formErrors.soilElevRiser = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'main-line-elevation':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.elevations.mainLineElevation = true;
          this.formValidation.formErrors.mainLineElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.mainLineElevation.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.elevations.mainLineElevation = false;
          this.formValidation.formErrors.mainLineElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'main-line-diameter':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.elevations.mainLineDiameter = true;
          this.formValidation.formErrors.mainLineDiameter = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.mainLineDiameter.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.elevations.mainLineDiameter = false;
          this.formValidation.formErrors.mainLineDiameter = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'rm-material':
        if (value === '') {
          this.formValidation.riserMaterial.rmMaterial = true;
          this.formValidation.formErrors.rmMaterial = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.riserMaterial.rmMaterial = false;
          this.formValidation.formErrors.rmMaterial = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'rm-pipe-size':
        if (value === '' || value <= 0) {
          this.formValidation.riserMaterial.rmPipeSize = true;
          this.formValidation.formErrors.rmPipeSize = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.riserMaterial.rmPipeSize = false;
          this.formValidation.formErrors.rmPipeSize = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'rm-pipe-size-box':
        if (value === '' || val <= 0 || val > 48) {
          this.formValidation.riserMaterial.rmPipeSize = true;
          this.formValidation.formErrors.rmPipeSize = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_101');
        } else {
          this.formValidation.riserMaterial.rmPipeSize = false;
          this.formValidation.formErrors.rmPipeSize = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'rm-manningsn':
        if (value === '' || val <= 0 || value === undefined || val > 1) {
          this.formValidation.riserMaterial.rmManningsn = true;
          this.formValidation.formErrors.rmManningsn = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_02');
        } else {
          this.formValidation.riserMaterial.rmManningsn = false;
          this.formValidation.formErrors.rmManningsn = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'guard':
        if (value === '') {
          this.formValidation.inletTop.guard = true;
          this.formValidation.formErrors.guard = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.inletTop.guard = false;
          this.formValidation.formErrors.guard = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'top-opening':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.inletTop.topOpening = true;
          this.formValidation.formErrors.topOpening = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.topOpening.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.inletTop.topOpening = false;
          this.formValidation.formErrors.topOpening = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'plugged-fraction':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.inletTop.pluggedFraction = true;
          this.formValidation.formErrors.pluggedFraction = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.pluggedFraction.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.inletTop.pluggedFraction = false;
          this.formValidation.formErrors.pluggedFraction = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'holes-per-ft':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.holesPerft = true;
          this.formValidation.formErrors.holesPerft = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.holesPerft.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.holesPerft = false;
          this.formValidation.formErrors.holesPerft = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'height':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.height = true;
          this.formValidation.formErrors.height = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.height.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.height = false;
          this.formValidation.formErrors.height = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'perf-diam':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.perfDiameter = true;
          this.formValidation.formErrors.perfDiameter = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.perfDiameter.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.perfDiameter = false;
          this.formValidation.formErrors.perfDiameter = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'perf-width':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.perfWidth = true;
          this.formValidation.formErrors.perfWidth = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.perfWidth.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.perfWidth = false;
          this.formValidation.formErrors.perfWidth = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'perf-height':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.perfHeight = true;
          this.formValidation.formErrors.perfHeight = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.perfHeight.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.perfHeight = false;
          this.formValidation.formErrors.perfHeight = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'plugged-perf-fraction':
        if (value === '' || val <= 0 || value === undefined) {
          this.formValidation.riserPerforations.perfFraction = true;
          this.formValidation.formErrors.perfFraction = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.perfFraction.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.riserPerforations.perfFraction = false;
          this.formValidation.formErrors.perfFraction = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'orifice-depth':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.orificePlate.useOrifice
        ) {
          this.formValidation.orificePlate.orificeDepth = true;
          this.formValidation.formErrors.orificeDepth = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.orificeDepth.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.orificePlate.orificeDepth = false;
          this.formValidation.formErrors.orificeDepth = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'diameter':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.orificePlate.useOrifice
        ) {
          this.formValidation.orificePlate.diameter = true;
          this.formValidation.formErrors.diameter = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.diameter.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.orificePlate.diameter = false;
          this.formValidation.formErrors.diameter = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'offset-length':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.offsetLength = true;
          this.formValidation.formErrors.offsetLength = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.offsetLength.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.offsetPipe.offsetLength = false;
          this.formValidation.formErrors.offsetLength = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'elbow-elevation':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.elbowElevation = true;
          this.formValidation.formErrors.elbowElevation = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.elbowElevation.replace(/(\s\([\w\W]*\))/, ''));
        } else {
          this.formValidation.offsetPipe.elbowElevation = false;
          this.formValidation.formErrors.elbowElevation = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'op-material':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.opMaterial = true;
          this.formValidation.formErrors.opMaterial = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.offsetPipe.opMaterial = false;
          this.formValidation.formErrors.opMaterial = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'op-pipe-size':
        if (
          (value === '' || val <= 0 || value === undefined) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.opPipeSize = true;
          this.formValidation.formErrors.opPipeSize = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.offsetPipe.opPipeSize = false;
          this.formValidation.formErrors.opPipeSize = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'op-pipe-size-box':
        if (
          (value === '' || val <= 0 || val > 48) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.opPipeSize = true;
          this.formValidation.formErrors.opPipeSize = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_101');
        } else {
          this.formValidation.offsetPipe.opPipeSize = false;
          this.formValidation.formErrors.opPipeSize = '';
          this.formValidation.hasError = false;
        }
        break;
      case 'op-manningsn':
        if (
          (value === '' || val <= 0 || value === undefined || val > 1) &&
          this.SurfaceInletDrainageFormData.offsetPipe.useOffset
        ) {
          this.formValidation.offsetPipe.opManningsn = true;
          this.formValidation.formErrors.opManningsn = ' ';
          this.formValidation.hasError = true;
          await MessageCenter.addNotificationFromCode('Formula_02');
        } else {
          this.formValidation.offsetPipe.opManningsn = false;
          this.formValidation.formErrors.opManningsn = '';
          this.formValidation.hasError = false;
        }
        break;
    }
  }
  @action.bound async validateReqInputWrapper(opt?) {
    switch (opt) {
      case 1:
        await this.validateInput('drained-area', this.SurfaceInletDrainageFormData.runoff.drainedArea);
        await this.validateInput('drainage-coefficient', this.SurfaceInletDrainageFormData.runoff.drainageCoefficient);
        if (this.formValidation.runoff.drainageCoefficient || this.formValidation.runoff.drainedArea) {
          return false;
        } else {
          return true;
        }

      case 2:
        await this.validateInput('water-elevation', this.SurfaceInletDrainageFormData.elevations.waterElev);
        await this.validateInput('soil-elevation-riser', this.SurfaceInletDrainageFormData.elevations.soilElevRiser);
        await this.validateInput('main-line-elevation', this.SurfaceInletDrainageFormData.elevations.mainLineElevation);
        await this.validateInput('main-line-diameter', this.SurfaceInletDrainageFormData.elevations.mainLineDiameter);
        await this.validateInput('rm-material', this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial);
        if (this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial !== 'USER_DEFINED') {
          await this.validateInput('rm-pipe-size', this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize);
        } else {
          await this.validateInput('rm-pipe-size-box', this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize);
        }
        await this.validateInput('rm-manningsn', this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn);
        await this.validateInput('guard', this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial);
        await this.validateInput('top-opening', this.SurfaceInletDrainageFormData.inletTop.topOpening);
        await this.validateInput('plugged-fraction', this.SurfaceInletDrainageFormData.inletTop.pluggedFraction);
        if (
          this.formValidation.elevations.waterElev ||
          this.formValidation.elevations.soilElevRiser ||
          this.formValidation.elevations.mainLineElevation ||
          this.formValidation.elevations.mainLineDiameter ||
          this.formValidation.riserMaterial.rmMaterial ||
          this.formValidation.riserMaterial.rmPipeSize ||
          this.formValidation.riserMaterial.rmManningsn ||
          this.formValidation.inletTop.guard ||
          this.formValidation.inletTop.topOpening ||
          this.formValidation.inletTop.pluggedFraction
        ) {
          return false;
        } else {
          return true;
        }
      case 3:
        if (this.roundPerfControl) {
          await this.validateInput('holes-per-ft', this.SurfaceInletDrainageFormData.riserPerforations.holesPerft);
          await this.validateInput('height', this.SurfaceInletDrainageFormData.riserPerforations.height);
          await this.validateInput('perf-diam', this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter);
          await this.validateInput('plugged-perf-fraction', this.SurfaceInletDrainageFormData.riserPerforations.perfFraction);
          if (
            this.formValidation.riserPerforations.holesPerft ||
            this.formValidation.riserPerforations.height ||
            this.formValidation.riserPerforations.perfDiameter ||
            this.formValidation.riserPerforations.perfFraction
          ) {
            return false;
          } else {
            return true;
          }
        }
        if (this.rectangularPerfControl) {
          await this.validateInput('holes-per-ft', this.SurfaceInletDrainageFormData.riserPerforations.holesPerft);
          await this.validateInput('height', this.SurfaceInletDrainageFormData.riserPerforations.height);
          await this.validateInput('perf-width', this.SurfaceInletDrainageFormData.riserPerforations.perfWidth);
          await this.validateInput('perf-height', this.SurfaceInletDrainageFormData.riserPerforations.perfHeight);
          await this.validateInput('plugged-perf-fraction', this.SurfaceInletDrainageFormData.riserPerforations.perfFraction);
        }
        if (
          this.formValidation.riserPerforations.holesPerft ||
          this.formValidation.riserPerforations.height ||
          this.formValidation.riserPerforations.perfWidth ||
          this.formValidation.riserPerforations.perfHeight ||
          this.formValidation.riserPerforations.perfFraction
        ) {
          return false;
        } else {
          return true;
        }
      case 4:
        await this.validateInput('orifice-depth', this.SurfaceInletDrainageFormData.orificePlate.orificeDepth);
        await this.validateInput('diameter', this.SurfaceInletDrainageFormData.orificePlate.diameter);
        if (this.formValidation.orificePlate.orificeDepth || this.formValidation.orificePlate.diameter) {
          return false;
        } else {
          return true;
        }
      case 5:
        await this.validateInput('offset-length', this.SurfaceInletDrainageFormData.offsetPipe.offsetLength);
        await this.validateInput('elbow-elevation', this.SurfaceInletDrainageFormData.offsetPipe.elbowElevation);
        await this.validateInput('op-material', this.SurfaceInletDrainageFormData.offsetPipe.opMaterial);
        if (this.SurfaceInletDrainageFormData.offsetPipe.opMaterial !== 'USER_DEFINED') {
          await this.validateInput('op-pipe-size', this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize);
        } else {
          await this.validateInput('op-pipe-size-box', this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize);
        }
        await this.validateInput('op-manningsn', this.SurfaceInletDrainageFormData.offsetPipe.opManningsn);
        if (
          this.formValidation.offsetPipe.offsetLength ||
          this.formValidation.offsetPipe.elbowElevation ||
          this.formValidation.offsetPipe.opMaterial ||
          this.formValidation.offsetPipe.opPipeSize ||
          this.formValidation.offsetPipe.opManningsn
        ) {
          return false;
        } else {
          return true;
        }
    }
  }
  @action.bound async validateReqInputStaging(opt?) {
    switch (opt) {
      case 1:
        const val = await this.validateReqInputWrapper(1);
        return val;
      case 2:
        const firstSec = await this.validateReqInputWrapper(1);
        const secondSec = await this.validateReqInputWrapper(2);
        return firstSec && secondSec;
      case 3:
        const first = await this.validateReqInputWrapper(1);
        const second = await this.validateReqInputWrapper(2);
        const third = await this.validateReqInputWrapper(3);
        return first && second && third;
      case 4:
        const one = await this.validateReqInputWrapper(1);
        const two = await this.validateReqInputWrapper(2);
        const three = await this.validateReqInputWrapper(3);
        const four = await this.validateReqInputWrapper(4);
        return one && two && three && four;
      case 5:
        const fstVar = await this.validateReqInputWrapper(1);
        const secVar = await this.validateReqInputWrapper(2);
        const thirdVar = await this.validateReqInputWrapper(3);
        const fourthVar = await this.validateReqInputWrapper(4);
        const fifthVar = await this.validateReqInputWrapper(5);
        return fstVar && secVar && thirdVar && fourthVar && fifthVar;
    }
  }
  // @action async validateReqInput() {}

  @action.bound getRequestBody(opt?) {
    switch (opt) {
      case 1:
        return `{
          "opt":1,
          "drainageArea" : ${this.SurfaceInletDrainageFormData.runoff.drainedArea},
          "drainageCoeff" : "${this.SurfaceInletDrainageFormData.runoff.drainageCoefficient}"
        }`;
      case 2:
        return `{
          "opt":2,
          "drainageArea" : ${this.SurfaceInletDrainageFormData.runoff.drainedArea},
          "drainageCoeff" : "${this.SurfaceInletDrainageFormData.runoff.drainageCoefficient}",
          "waterElev" : ${this.SurfaceInletDrainageFormData.elevations.waterElev},
          "soilElevAtRiser" : ${this.SurfaceInletDrainageFormData.elevations.soilElevRiser},
          "mainLineElev" : ${this.SurfaceInletDrainageFormData.elevations.mainLineElevation},
          "mainLineDia" : ${this.SurfaceInletDrainageFormData.elevations.mainLineDiameter},
          "riserMaterial" : "${this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial}",
          "riserPerforated" : ${this.SurfaceInletDrainageFormData.riserMaterial.perforated},
          "riserPressurizable" : ${this.SurfaceInletDrainageFormData.riserMaterial.pressurizable},
          "riserPipeSize" : ${this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize},
          "riserPipeManningsN" : ${this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn},
          "inletTopGuard" : "${this.SurfaceInletDrainageFormData.inletTop.guard}",
          "topOpening" : ${this.SurfaceInletDrainageFormData.inletTop.topOpening},
          "plugedFraction" : ${this.SurfaceInletDrainageFormData.inletTop.pluggedFraction}
      }`;
      case 3:
        return `{
          "opt":3,
          "drainageArea" : ${this.SurfaceInletDrainageFormData.runoff.drainedArea},
          "drainageCoeff" : "${this.SurfaceInletDrainageFormData.runoff.drainageCoefficient}",
          "waterElev": ${this.SurfaceInletDrainageFormData.elevations.waterElev},
          "soilElevAtRiser": ${this.SurfaceInletDrainageFormData.elevations.soilElevRiser},
          "mainLineElev": ${this.SurfaceInletDrainageFormData.elevations.mainLineElevation},
          "mainLineDia": ${this.SurfaceInletDrainageFormData.elevations.mainLineDiameter},
          "riserMaterial": "${this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial}",
          "riserPerforated": ${this.SurfaceInletDrainageFormData.riserMaterial.perforated},
          "riserPressurizable": ${this.SurfaceInletDrainageFormData.riserMaterial.pressurizable},
          "riserPipeSize": "${this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize}",
          "riserPipeManningsN": ${this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn},
          "inletTopGuard": "${this.SurfaceInletDrainageFormData.inletTop.guard}",
          "topOpening": ${this.SurfaceInletDrainageFormData.inletTop.topOpening},
          "plugedFraction":${this.SurfaceInletDrainageFormData.inletTop.pluggedFraction},
          "holePerFt": ${this.SurfaceInletDrainageFormData.riserPerforations.holesPerft},
          "riserHeight": ${this.SurfaceInletDrainageFormData.riserPerforations.height},
          "roundPerf": ${this.roundPerfControl},
          "rectangularPerf": ${this.roundPerfControl},
          "perfDiam": ${this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter},
          "perfwidth": ${this.SurfaceInletDrainageFormData.riserPerforations.perfWidth},
          "perfH": ${this.SurfaceInletDrainageFormData.riserPerforations.perfHeight},
          "perfFraction": ${this.SurfaceInletDrainageFormData.riserPerforations.perfFraction}
      }`;
      case 4:
        return `{
          "opt" : 4,
          "drainageArea" : ${this.SurfaceInletDrainageFormData.runoff.drainedArea},
          "drainageCoeff" : "${this.SurfaceInletDrainageFormData.runoff.drainageCoefficient}",
          "waterElev": ${this.SurfaceInletDrainageFormData.elevations.waterElev},
          "soilElevAtRiser": ${this.SurfaceInletDrainageFormData.elevations.soilElevRiser},
          "mainLineElev": ${this.SurfaceInletDrainageFormData.elevations.mainLineElevation},
          "mainLineDia": ${this.SurfaceInletDrainageFormData.elevations.mainLineDiameter},
          "riserMaterial": "${this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial}",
          "riserPerforated": ${this.SurfaceInletDrainageFormData.riserMaterial.perforated},
          "riserPressurizable": ${this.SurfaceInletDrainageFormData.riserMaterial.pressurizable},
          "riserPipeSize": "${this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize}",
          "riserPipeManningsN": ${this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn},
          "inletTopGuard": "${this.SurfaceInletDrainageFormData.inletTop.guard}",
          "topOpening": ${this.SurfaceInletDrainageFormData.inletTop.topOpening},
          "plugedFraction":${this.SurfaceInletDrainageFormData.inletTop.pluggedFraction},
          "holePerFt": ${this.SurfaceInletDrainageFormData.riserPerforations.holesPerft},
          "riserHeight": ${this.SurfaceInletDrainageFormData.riserPerforations.height},
          "roundPerf": ${this.roundPerfControl},
          "rectangularPerf": ${this.roundPerfControl},
          "perfDiam": ${this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter},
          "perfwidth": ${this.SurfaceInletDrainageFormData.riserPerforations.perfWidth},
          "perfH": ${this.SurfaceInletDrainageFormData.riserPerforations.perfHeight},
          "perfFraction": ${this.SurfaceInletDrainageFormData.riserPerforations.perfFraction},
          "orifice":${this.SurfaceInletDrainageFormData.orificePlate.useOrifice},
          "orificeDepth":${this.SurfaceInletDrainageFormData.orificePlate.orificeDepth},
          "orificeDiam":${this.SurfaceInletDrainageFormData.orificePlate.diameter}   
      }`;
      case 5:
        return `{
          "opt" : 5,
          "drainageArea" : ${this.SurfaceInletDrainageFormData.runoff.drainedArea},
          "drainageCoeff" : "${this.SurfaceInletDrainageFormData.runoff.drainageCoefficient}",
          "waterElev": ${this.SurfaceInletDrainageFormData.elevations.waterElev},
          "soilElevAtRiser": ${this.SurfaceInletDrainageFormData.elevations.soilElevRiser},
          "mainLineElev": ${this.SurfaceInletDrainageFormData.elevations.mainLineElevation},
          "mainLineDia": ${this.SurfaceInletDrainageFormData.elevations.mainLineDiameter},
          "riserMaterial": "${this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial}",
          "riserPerforated": ${this.SurfaceInletDrainageFormData.riserMaterial.perforated},
          "riserPressurizable": ${this.SurfaceInletDrainageFormData.riserMaterial.pressurizable},
          "riserPipeSize": "${this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize}",
          "riserPipeManningsN": ${this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn},
          "inletTopGuard": "${this.SurfaceInletDrainageFormData.inletTop.guard}",
          "topOpening": ${this.SurfaceInletDrainageFormData.inletTop.topOpening},
          "plugedFraction":${this.SurfaceInletDrainageFormData.inletTop.pluggedFraction},
          "holePerFt": ${this.SurfaceInletDrainageFormData.riserPerforations.holesPerft},
          "riserHeight": ${this.SurfaceInletDrainageFormData.riserPerforations.height},
          "roundPerf": ${this.roundPerfControl},
          "rectangularPerf": ${this.roundPerfControl},
          "perfDiam": ${this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter},
          "perfwidth": ${this.SurfaceInletDrainageFormData.riserPerforations.perfWidth},
          "perfH": ${this.SurfaceInletDrainageFormData.riserPerforations.perfHeight},
          "perfFraction": ${this.SurfaceInletDrainageFormData.riserPerforations.perfFraction},
          "orifice":${this.SurfaceInletDrainageFormData.orificePlate.useOrifice},
          "orificeDepth":${this.SurfaceInletDrainageFormData.orificePlate.orificeDepth},
          "orificeDiam":${this.SurfaceInletDrainageFormData.orificePlate.diameter},  
          "offsetPipe":${this.SurfaceInletDrainageFormData.offsetPipe.useOffset},
          "offsetLength":${this.SurfaceInletDrainageFormData.offsetPipe.offsetLength},
          "elbowElev":${this.SurfaceInletDrainageFormData.offsetPipe.elbowElevation},
          "offsetMaterial":"${this.SurfaceInletDrainageFormData.offsetPipe.opMaterial}",
          "offsetPressurizable":${this.SurfaceInletDrainageFormData.offsetPipe.opPressurizable},
          "offsetPerforated": ${this.SurfaceInletDrainageFormData.offsetPipe.opPerforated},
          "offsetPipeSize":"${this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize}",
          "offsetPipeManningsN":${this.SurfaceInletDrainageFormData.offsetPipe.opManningsn}
      }`;
    }
  }

  @action.bound getReqBodyCalculation(): SurfaceInletRequestDTO {
    return {
      drainageArea: parseFloat(this.SurfaceInletDrainageFormData.runoff.drainedArea),
      drainageCoeff: this.SurfaceInletDrainageFormData.runoff.drainageCoefficient,
      waterElev: parseFloat(this.SurfaceInletDrainageFormData.elevations.waterElev),
      soilElevAtRiser: parseFloat(this.SurfaceInletDrainageFormData.elevations.soilElevRiser),
      mainLineElev: parseFloat(this.SurfaceInletDrainageFormData.elevations.mainLineElevation),
      mainLineDia: parseFloat(this.SurfaceInletDrainageFormData.elevations.mainLineDiameter),
      riserMaterial: this.SurfaceInletDrainageFormData.riserMaterial.rmMaterial,
      riserPerforated: this.SurfaceInletDrainageFormData.riserMaterial.perforated,
      riserPressurizable: this.SurfaceInletDrainageFormData.riserMaterial.pressurizable,
      riserPipeSize: parseFloat(this.SurfaceInletDrainageFormData.riserMaterial.rmPipeSize),
      riserPipeManningsN: parseFloat(this.SurfaceInletDrainageFormData.riserMaterial.rmManningsn),
      inletTopGuard: this.SurfaceInletDrainageFormData.inletTop.guard,
      topOpening: parseFloat(this.SurfaceInletDrainageFormData.inletTop.topOpening),
      plugedFraction: parseFloat(this.SurfaceInletDrainageFormData.inletTop.pluggedFraction),
      holePerFt: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.holesPerft),
      riserHeight: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.height),
      roundPerf: this.roundPerfControl,
      rectangularPerf: this.rectangularPerfControl,
      perfDiam: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.perfDiameter),
      perfwidth: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.perfWidth),
      perfH: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.perfHeight),
      perfFraction: parseFloat(this.SurfaceInletDrainageFormData.riserPerforations.perfFraction),
      orifice: this.SurfaceInletDrainageFormData.orificePlate.useOrifice,
      orificeDepth: parseFloat(this.SurfaceInletDrainageFormData.orificePlate.orificeDepth),
      orificeDiam: parseFloat(this.SurfaceInletDrainageFormData.orificePlate.diameter),
      offsetPipe: this.SurfaceInletDrainageFormData.offsetPipe.useOffset,
      offsetLength: parseFloat(this.SurfaceInletDrainageFormData.offsetPipe.offsetLength),
      elbowElev: parseFloat(this.SurfaceInletDrainageFormData.offsetPipe.elbowElevation),
      offsetMaterial: this.SurfaceInletDrainageFormData.offsetPipe.opMaterial === '' ? 'USER_DEFINED' : this.SurfaceInletDrainageFormData.offsetPipe.opMaterial,
      offsetPressurizable: this.SurfaceInletDrainageFormData.offsetPipe.opPressurizable,
      offsetPerforated: this.SurfaceInletDrainageFormData.offsetPipe.opPerforated,
      offsetPipeSize: parseFloat(this.SurfaceInletDrainageFormData.offsetPipe.opPipeSize),
      offsetPipeManningsN: parseFloat(this.SurfaceInletDrainageFormData.offsetPipe.opManningsn),
    };
  }

  @action.bound async calSurfaceInletStaging(opt) {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls(['Common_26']);
    this.SurfaceInletDrainageFormData.runoff.qRequired = "0";
    this.SurfaceInletDrainageFormData.inletTop.qTop = "0";
    this.SurfaceInletDrainageFormData.riserPerforations.qPerf = '0';
    this.SurfaceInletDrainageFormData.orificePlate.qOrifice = '';
    this.SurfaceInletDrainageFormData.offsetPipe.qOffset = '';
    const validationPass = await this.validateReqInputStaging(opt);
    if (validationPass) {
      const requestBody = this.getRequestBody(opt) as SurfaceInletRequestDTO;
      try {
        const response = await HydraulicFormulaController.calculateSurfaceInletUsingPOST(opt, requestBody as string);
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data && response.data.outputInfo) {
          this.SurfaceInletDrainageFormData.runoff.qRequired = isNaN(parseFloat(response.data.requiredQ!)) ? "0.00" : (response.data.requiredQ!);
          this.SurfaceInletDrainageFormData.inletTop.qTop = isNaN(parseFloat(response.data.topQ!)) ? "0.00" : (response.data.topQ!);
          this.SurfaceInletDrainageFormData.riserPerforations.qPerf = response.data.perfQ!.toLowerCase() === 'nan' ? '0' : response.data.perfQ!;
          this.SurfaceInletDrainageFormData.orificePlate.qOrifice = response.data.orificeQ!.toLowerCase() === 'nan' ? '' : response.data.orificeQ!;
          this.SurfaceInletDrainageFormData.offsetPipe.qOffset = response.data.offsetQ!.toLowerCase() === 'nan' ? '' : response.data.offsetQ!;
        } else if (
          response?.data?.errorResponse?.errorMessage !== undefined &&
          response?.data?.errorResponse?.errorType !== undefined
        ) {
          MessageCenter.resetMessageCenter();
          MessageCenter.addNotification(
            response?.data?.errorResponse?.errorMessage,
            response?.data?.errorResponse?.errorType,
            shortCode
          );
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
    }
    MessageCenter.unblockCalls();
    this.isLoading = false;
    this.disableComputeBtn = false;
  }

  @action.bound async computefnSurfaceInletDrainage() {
    this.disableComputeBtn = true;
    this.disablePrintBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls([]);
    const validationPass = await this.validateReqInputStaging(5);
    if (validationPass) {
      const requestBody = this.getReqBodyCalculation();
      try {
        const response = await HydraulicFormulaController.calculateSurfaceInletUsingPOST1(requestBody);
        const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
        if (response && response.data && response.data.outputInfo) {
          this.SurfaceInletDrainageResults = [];
          this.hasResults = true;
          this.disablePrintBtn = false;
          response.data.outputInfo
            ?.replace(/(, Q)/g, '\nQ').split('\n')
            .filter((item) => item !== '')
            .map((item, index) => {
              this.SurfaceInletDrainageResults.push({
                label: item.split('=')[0].trim(),
                value: item.slice(item.indexOf('=') + 1),
              });
            });
          this.SurfaceInletDrainageFormData.runoff.qRequired = (response.data.requiredQ!);
          this.SurfaceInletDrainageFormData.inletTop.qTop = (response.data.topQ!);
          this.SurfaceInletDrainageFormData.riserPerforations.qPerf = response.data.perfQ!;
          this.SurfaceInletDrainageFormData.orificePlate.qOrifice = response.data.orificeQ!;
          this.SurfaceInletDrainageFormData.offsetPipe.qOffset = response.data.offsetQ!;
          this.surfaceInletDrainageFormInputSnapshot = {
            runoff: { ...this.SurfaceInletDrainageFormData.runoff },
            elevations: { ...this.SurfaceInletDrainageFormData.elevations },
            riserMaterial: { ...this.SurfaceInletDrainageFormData.riserMaterial },
            inletTop: { ...this.SurfaceInletDrainageFormData.inletTop },
            riserPerforations: { ...this.SurfaceInletDrainageFormData.riserPerforations },
            orificePlate: { ...this.SurfaceInletDrainageFormData.orificePlate },
            offsetPipe: { ...this.SurfaceInletDrainageFormData.offsetPipe },
          };
        } else if (
          response?.data?.errorResponse?.errorMessage !== undefined &&
          response?.data?.errorResponse?.errorType !== undefined
        ) {
          MessageCenter.resetMessageCenter();
          MessageCenter.addNotification(
            response?.data?.errorResponse?.errorMessage,
            response?.data?.errorResponse?.errorType,
            shortCode
          );
          this.hasResults = false;
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      // If we are missing required inputs or they are invalid
      // If field validation has been added, input specific messages should not be shown at this time.
      MessageCenter.resetMessageCenter();
      MessageCenter.unblockCalls();
      await MessageCenter.addNotificationFromCode('Common_26');
      this.hasResults = false;
    }
    this.disableComputeBtn = false;
    this.isLoading = false;
    MessageCenter.unblockCalls();
  }

  private orificeInputUsed(label) {
    return label !== 'Use Orifice' && (label.includes('Orifice') || label === 'Diameter (in)') && !this.surfaceInletDrainageFormInputSnapshot?.orificePlate.useOrifice;
  }
  private offsetInputUsed(label) {
    return label !== 'Use Offset' && (label.includes('Offset') || label === 'Elbow Elevation (ft)') && !this.surfaceInletDrainageFormInputSnapshot?.offsetPipe.useOffset;
  }
  private roundPerfUsed(label) {
    return (this.roundPerfControl && (label === this.formLabelsForExport.perfWidth || label === this.formLabelsForExport.perfHeight));
  }
  private rectPerfUsed(label) {
    return this.rectangularPerfControl && (label === this.formLabelsForExport.perfDiameter);
  }

  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    for (const childProperty in this.formLabelsForExport) {
      if (this.formLabelsForExport.hasOwnProperty(childProperty)) {
        const label = this.formLabelsForExport[childProperty];
        if (this.orificeInputUsed(label) || this.offsetInputUsed(label) || this.roundPerfUsed(label) || this.rectPerfUsed(label)) {
          continue;
        }
        if (label === this.formLabelsForExport.roundPerf && this.roundPerfControl) {
          this.exportedInputs.push({
            label: label,
            value: this.roundPerfControl ? "Selected" : "Unselected",
          });
          continue;
        }
        else if (label === this.formLabelsForExport.rectangularPerf && this.rectangularPerfControl) {
          this.exportedInputs.push({
            label: label,
            value: this.rectangularPerfControl ? "Selected" : "Unselected",
          });
          continue;
        }
        for (const nestedProperty in this.surfaceInletDrainageFormInputSnapshot) {
          if (this.surfaceInletDrainageFormInputSnapshot.hasOwnProperty(nestedProperty)) {
            const childObj = this.surfaceInletDrainageFormInputSnapshot[nestedProperty];
            if (childObj.hasOwnProperty(childProperty)) {
              const value = childObj[childProperty];
              if (label && value !== '') {
                if (typeof value === 'string') {
                  this.exportedInputs.push({
                    label: label,
                    value: label.includes('- Material') || label === 'Guard' ? toTitleFromUnderscore(value) : value
                  });
                }
                else {
                  this.exportedInputs.push({
                    label: label,
                    value: value ? 'Checked' : 'Unchecked'
                  });
                }
              }
            }
          }
        }
      }
    }

    this.exportedResults = [...this.SurfaceInletDrainageResults];
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(): void {
    // Dropdowns
    this.SurfaceInletDrainageRunoffOptions = (this.defaultValues.drainageCoeff as string[]).map((value, index) => ({
      value: value,
      name: value,
    }));
    this.SurfaceInletDrainageInletTopOptions = (this.defaultValues.inletTopGuard as string[]).map((value, index) => ({
      value: value,
      name: toTitleFromUnderscore(value),
    }));
    this.SurfaceInletDrainageRmMaterialOptions = this.defaultValues.riserMaterial.map((value, index) => ({
      key: index,
      value: value.riserMaterialName,
      name: toTitleFromUnderscore(value.riserMaterialName),
    }));
    this.SurfaceInletDrainageOpMaterialOptions = this.defaultValues.offsetMaterial.map((value, index) => ({
      key: index,
      value: value.offsetMaterialName,
      name: toTitleFromUnderscore(value.offsetMaterialName),
    }));
    // Radio buttons
    if (this.defaultValues.roundPerf) {
      this.surfaceInletRadioControl = 0;
    } else {
      this.surfaceInletRadioControl = 1;
    }
    // Main inputs
    this.SurfaceInletDrainageFormData = {
      runoff: {
        drainedArea: this.defaultValues.drainageArea,
        drainageCoefficient: '',
        qRequired: "0.00",
      },
      elevations: {
        waterElev: this.defaultValues.waterElev,
        soilElevRiser: this.defaultValues.soilElevAtRiser,
        mainLineElevation: this.defaultValues.mainLineElev,
        mainLineDiameter: this.defaultValues.mainLineDia,
      },
      riserMaterial: {
        perforated: this.defaultValues.riserPerforated,
        rmMaterial: '',
        pressurizable: this.defaultValues.riserPressurizable,
        rmPipeSize: '0.00',
        rmManningsn: "0.0000",
      },
      inletTop: {
        guard: '',
        topOpening: this.roundingHelperString(this.defaultValues.topOpening, 2),
        enableTopOpening: false,
        pluggedFraction: this.roundingHelperString(this.defaultValues.plugedFraction, 2),
        qTop: "0.00",
      },
      riserPerforations: {
        holesPerft: this.roundingHelperString(this.defaultValues.holePerFt, 2),
        height: this.roundingHelperString(this.defaultValues.riserHeight, 2),
        perfDiameter: this.roundingHelperString(this.defaultValues.perfDiam, 2),
        perfWidth: this.roundingHelperString(this.defaultValues.perfwidth, 2),
        perfHeight: this.roundingHelperString(this.defaultValues.perfH, 2),
        perfFraction: this.roundingHelperString(this.defaultValues.perfFraction, 2),
        qPerf: '0.00',
      },
      orificePlate: {
        useOrifice: this.defaultValues.orifice,
        orificeDepth: this.roundingHelperString(this.defaultValues.orificeDepth, 2),
        userDefinedSize: true,
        diameter: this.roundingHelperString(this.defaultValues.orificeDiam, 2),
        qOrifice: '',
      },
      offsetPipe: {
        useOffset: this.defaultValues.offsetPipe,
        offsetLength: this.roundingHelperString(this.defaultValues.offsetLength, 2),
        elbowElevation: this.roundingHelperString(this.defaultValues.elbowElev, 2),
        opPerforated: this.defaultValues.offsetPerforated,
        opMaterial: '',
        opPressurizable: this.defaultValues.offsetPressurizable,
        opPipeSize: "0.00",
        opManningsn: "0.0000",
        qOffset: '',
      },
    };
  }

  @action.bound handleOnKeyDown(event) {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  }
  @action.bound noFunction() {
    return;
  }

  @action.bound calculateRunoff() { }
}
